import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LayoutGrid, Users, Clock, Calendar, FileCheck, CreditCard, ArrowRightLeft, Settings, LogOut, Menu, X, FileText, BarChart3, Calculator, Fingerprint, ShieldCheck, ChevronDown, ChevronRight, CalendarDays, UserCircle, SlidersHorizontal, Receipt, Table2, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/utils/api';

const Badge = ({ count, className = '' }) => {
  if (!count || count <= 0) return null;
  return (
    <span className={`ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 text-[11px] font-semibold rounded-full bg-red-500 text-white ${className}`} data-testid="badge-count">
      {count > 99 ? '99+' : count}
    </span>
  );
};

const Layout = ({ user, onLogout }) => {
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [shiftProOpen, setShiftProOpen] = useState(
    location.pathname.startsWith('/shiftpro')
  );
  const [badges, setBadges] = useState({ mps_approvals: 0, shiftpro_approvals: 0, shiftpro_detail: {} });

  const fetchBadges = useCallback(async () => {
    try {
      const res = await api.get(`/badge-counts?role=${user?.role || ''}`);
      setBadges(res.data || {});
    } catch { /* silent */ }
  }, [user?.role]);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 60000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  useEffect(() => { fetchBadges(); }, [location.pathname]);

  const navigation = [
    { name: 'Dashboard', path: '/', icon: LayoutGrid, roles: ['HR', 'Manager', 'CEO', 'Employee'] },
    { name: 'Employees', path: '/employees', icon: Users, roles: ['HR', 'Manager', 'CEO'] },
    { name: 'Daily Attendance', path: '/attendance', icon: Clock, roles: [] },
    { name: 'Monthly Grid', path: '/bulk-attendance', icon: Calendar, roles: ['HR', 'Manager'] },
    { name: 'Monthly Grid 2', path: '/monthly-grid-2', icon: Table2, roles: ['HR', 'Manager'] },
    { name: 'Salary Process 1', path: '/salary-process-1', icon: Calculator, roles: ['HR'] },
    { name: 'Salary Process 2', path: '/salary-process-v2', icon: Calculator, roles: ['HR', 'Manager'] },
    { name: 'Transfer Funds', path: '/transfer-funds', icon: ArrowRightLeft, roles: ['HR', 'Manager', 'CEO', 'Account'] },
    { name: 'Salary Slips', path: '/salary-slips', icon: FileText, roles: ['HR', 'Account'] },
    { name: 'Reports', path: '/reports', icon: BarChart3, roles: ['HR', 'Manager', 'CEO'] },
    { name: 'Approvals', path: '/approvals', icon: FileCheck, roles: ['Manager', 'CEO'], badgeKey: 'mps_approvals' },
    { name: 'Payments', path: '/payments', icon: CreditCard, roles: ['CEO'] },
    { name: 'Download MPS', path: '/download-mps', icon: Download, roles: ['CEO'] },
    { name: 'Settings', path: '/settings', icon: Settings, roles: ['HR', 'Manager', 'CEO'] },
  ];

  const shiftProChildren = [
    { name: 'Attendance', path: '/shiftpro-attendance', icon: Fingerprint, roles: ['HR', 'Manager', 'CEO'] },
    { name: 'Approvals', path: '/shiftpro-approvals', icon: ShieldCheck, badgeKey: 'shiftpro_approvals', roles: ['HR', 'Manager', 'CEO'] },
    { name: 'Rosters', path: '/shiftpro-rosters', icon: CalendarDays, roles: ['HR', 'Manager', 'CEO'] },
    { name: 'Employees Data', path: '/shiftpro-employees', icon: UserCircle, roles: ['HR', 'Manager', 'CEO'] },
    { name: 'Approval Settings', path: '/shiftpro-settings', icon: SlidersHorizontal, roles: ['HR', 'Manager', 'CEO'] },
    { name: 'Salary Slip', path: '/shiftpro-salary-slip', icon: Receipt, roles: ['Employee'] },
  ];

  const shiftProRoles = ['HR', 'Manager', 'CEO', 'Employee'];
  const showShiftPro = shiftProRoles.includes(user.role);
  const isShiftProActive = location.pathname.startsWith('/shiftpro');
  const filteredNav = navigation.filter(item => item.roles.includes(user.role));
  const shiftProTotal = badges.shiftpro_approvals || 0;

  return (
    <div className="min-h-screen" style={{ background: 'var(--apple-bg, #F5F5F7)' }}>
      {/* Mobile menu */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b px-4 py-3 flex items-center justify-between" style={{ borderColor: 'var(--apple-border)' }}>
        <div className="flex items-center gap-2">
          <img src="/logo.png" alt="Mr. Chow's" className="h-7" />
          <h1 className="text-[15px] font-semibold" style={{ color: 'var(--apple-text)' }}>MPS</h1>
        </div>
        <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg hover:bg-black/5" data-testid="mobile-menu-button">
          {sidebarOpen ? <X className="h-5 w-5" style={{ color: 'var(--apple-secondary)' }} /> : <Menu className="h-5 w-5" style={{ color: 'var(--apple-secondary)' }} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-[260px] bg-white border-r transform transition-transform duration-200 ease-in-out lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}
        style={{ borderColor: 'var(--apple-border)' }}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="px-5 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <img src="/logo.png" alt="Mr. Chow's" className="h-9 rounded-lg" data-testid="sidebar-logo" />
              <div>
                <h1 className="text-[17px] font-semibold tracking-tight" style={{ color: 'var(--apple-text)' }}>MPS</h1>
                <p className="text-[12px]" style={{ color: 'var(--apple-secondary)' }}>Mr. Chow's</p>
              </div>
            </div>
          </div>

          {/* Nav */}
          <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto">
            {filteredNav.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const badgeCount = item.badgeKey ? badges[item.badgeKey] || 0 : 0;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className="flex items-center gap-3 px-3 py-[9px] rounded-[8px] text-[13px] font-medium transition-colors"
                  style={{
                    color: isActive ? 'var(--apple-blue)' : 'var(--apple-text)',
                    background: isActive ? 'var(--apple-blue-10)' : 'transparent',
                  }}
                  onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = '#F5F5F7'; }}
                  onMouseLeave={e => { if (!isActive) e.currentTarget.style.background = 'transparent'; }}
                  data-testid={`nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  <Icon className="h-[18px] w-[18px]" style={{ color: isActive ? 'var(--apple-blue)' : 'var(--apple-secondary)' }} />
                  {item.name}
                  <Badge count={badgeCount} />
                </Link>
              );
            })}

            {/* ShiftPro */}
            {showShiftPro && (
              <div className="pt-2" data-testid="shiftpro-tree">
                <button
                  onClick={() => setShiftProOpen(!shiftProOpen)}
                  className="w-full flex items-center gap-3 px-3 py-[9px] rounded-[8px] text-[13px] font-medium transition-colors"
                  style={{
                    color: isShiftProActive ? 'var(--apple-blue)' : 'var(--apple-text)',
                    background: isShiftProActive ? 'var(--apple-blue-10)' : 'transparent',
                  }}
                  data-testid="nav-shiftpro"
                >
                  <BarChart3 className="h-[18px] w-[18px]" style={{ color: isShiftProActive ? 'var(--apple-blue)' : 'var(--apple-secondary)' }} />
                  <span className="flex-1 text-left">ShiftPro</span>
                  {shiftProTotal > 0 && !shiftProOpen && <Badge count={shiftProTotal} />}
                  {shiftProOpen ? <ChevronDown className="h-4 w-4" style={{ color: 'var(--apple-secondary)' }} /> : <ChevronRight className="h-4 w-4" style={{ color: 'var(--apple-secondary)' }} />}
                </button>

                {shiftProOpen && (
                  <div className="ml-5 mt-0.5 pl-3 space-y-0.5" style={{ borderLeft: '1px solid var(--apple-border)' }}>
                    {shiftProChildren.filter(child => !child.roles || child.roles.includes(user.role)).map((child) => {
                      const ChildIcon = child.icon;
                      const isChildActive = location.pathname === child.path;
                      const childBadge = child.badgeKey ? badges[child.badgeKey] || 0 : 0;
                      return (
                        <Link
                          key={child.path}
                          to={child.path}
                          onClick={() => setSidebarOpen(false)}
                          className="flex items-center gap-2.5 px-3 py-[7px] rounded-[8px] text-[13px] transition-colors"
                          style={{
                            color: isChildActive ? 'var(--apple-blue)' : '#636366',
                            fontWeight: isChildActive ? 600 : 400,
                            background: isChildActive ? 'var(--apple-blue-10)' : 'transparent',
                          }}
                          onMouseEnter={e => { if (!isChildActive) e.currentTarget.style.background = '#F5F5F7'; }}
                          onMouseLeave={e => { if (!isChildActive) e.currentTarget.style.background = 'transparent'; }}
                          data-testid={`nav-shiftpro-${child.name.toLowerCase()}`}
                        >
                          <ChildIcon className="h-4 w-4" style={{ color: isChildActive ? 'var(--apple-blue)' : '#AEAEB2' }} />
                          {child.name}
                          {childBadge > 0 && <Badge count={childBadge} />}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </nav>

          {/* User footer */}
          <div className="p-4">
            <div className="p-3 rounded-[10px] border" style={{ borderColor: 'var(--apple-border)' }}>
              <p className="text-[13px] font-semibold" style={{ color: 'var(--apple-text)' }}>{user.name}</p>
              <p className="text-[11px] mt-0.5" style={{ color: 'var(--apple-secondary)' }}>{user.email}</p>
              <span className="inline-block mt-2 px-2.5 py-[2px] text-[11px] font-semibold rounded-full"
                style={{ background: 'var(--apple-blue-10)', color: 'var(--apple-blue)' }}>
                {user.role}
              </span>
            </div>
            <button
              onClick={onLogout}
              className="w-full mt-2 flex items-center gap-2 px-3 py-2 rounded-[8px] text-[13px] transition-colors hover:bg-[#F5F5F7]"
              style={{ color: 'var(--apple-secondary)' }}
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main */}
      <main className="lg:ml-[260px] min-h-screen pt-16 lg:pt-0">
        <div className="p-4 sm:p-6 md:p-8 max-w-[1400px]">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default Layout;
